import { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * Custom hook for managing expense categories
 * Provides categories for dropdowns and settings management
 */
export function useCategories() {
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/settings/categories');
      setCategories(response.data.categories || {});
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err.message);
      // Fallback to default categories
      setCategories({
        'Food & Dining': {
          subcategories: ['Groceries', 'Restaurants', 'Takeaway', 'Coffee & Snacks'],
          budget_group: 'needs'
        },
        'Transportation': {
          subcategories: ['Public Transport', 'Fuel', 'Parking', 'Vehicle Maintenance'],
          budget_group: 'needs'
        },
        'Housing': {
          subcategories: ['Rent', 'Utilities', 'Internet', 'Maintenance', 'Insurance'],
          budget_group: 'needs'
        },
        'Health & Fitness': {
          subcategories: ['Medical', 'Gym', 'Pharmacy', 'Wellness'],
          budget_group: 'needs'
        },
        'Shopping': {
          subcategories: ['Clothing', 'Electronics', 'Home Goods', 'Personal Care'],
          budget_group: 'wants'
        },
        'Entertainment': {
          subcategories: ['Movies', 'Games', 'Hobbies', 'Events'],
          budget_group: 'wants'
        },
        'Education': {
          subcategories: ['Courses', 'Books', 'Supplies'],
          budget_group: 'needs'
        },
        'Other': {
          subcategories: ['Miscellaneous', 'Gifts', 'Donations'],
          budget_group: 'wants'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const updateCategories = async (newCategories) => {
    try {
      const response = await api.put('/settings/categories', {
        categories: newCategories
      });
      setCategories(response.data.categories);
      return { success: true };
    } catch (err) {
      console.error('Error updating categories:', err);
      return { success: false, error: err.message };
    }
  };

  const getCategoriesByBudgetGroup = (budgetGroup) => {
    return Object.entries(categories)
      .filter(([_, data]) => data.budget_group === budgetGroup)
      .reduce((acc, [name, data]) => {
        acc[name] = data;
        return acc;
      }, {});
  };

  const getAllCategoryNames = () => {
    return Object.keys(categories).sort();
  };

  const getSubcategories = (categoryName) => {
    return categories[categoryName]?.subcategories || [];
  };

  const getBudgetGroup = (categoryName) => {
    return categories[categoryName]?.budget_group || 'wants';
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    error,
    fetchCategories,
    updateCategories,
    getCategoriesByBudgetGroup,
    getAllCategoryNames,
    getSubcategories,
    getBudgetGroup
  };
}
